import React, { useState } from 'react';
import { X, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '../api';

function LiveBetSlip({ slip, onUpdateSlip, onRemove, onClear, onClose, currentOdds, freePlayBalance, onBetPlaced, user, showToast }) {
  const [amount, setAmount] = useState('');
  const [isParlayMode, setIsParlayMode] = useState(false);
  const [useFreePlay, setUseFreePlay] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState(null);

  // Enrich each slip item with live staleness data
  const enriched = slip.map((item) => {
    const game = currentOdds.find((g) => g.id === item.game.id);
    if (!game) return { ...item, stale: false };
    const bm = game.bookmakers?.[0];
    if (!bm) return { ...item, stale: false };
    const market = bm.markets?.find((m) => m.key === item.market.key);
    if (!market) return { ...item, stale: false };
    const outcome = market.outcomes?.find((o) => o.name === item.outcome.name);
    if (!outcome) return { ...item, stale: false };
    const newPrice = outcome.price;
    const stale = Math.abs(newPrice - item.price) > 0.005;
    return { ...item, stale, new_price: newPrice, new_american: outcome.american };
  });

  const hasStale = enriched.some((i) => i.stale);
  const isParlay = isParlayMode && slip.length >= 2;

  // Combined parlay odds
  const parlayDecimal = enriched.reduce((acc, i) => acc * (i.stale ? (i.new_price ?? i.price) : i.price), 1);
  const parlayAmerican = parlayDecimal >= 2.0
    ? `+${Math.round((parlayDecimal - 1) * 100)}`
    : `${Math.round(-100 / (parlayDecimal - 1))}`;

  const betAmt = parseFloat(amount) || 0;

  // To-win calculation
  const toWin = (() => {
    if (betAmt <= 0) return null;
    if (isParlay) {
      return ((betAmt * parlayDecimal) - betAmt).toFixed(2);
    }
    if (slip.length === 1) {
      const price = enriched[0]?.stale ? (enriched[0].new_price ?? enriched[0].price) : enriched[0]?.price;
      if (!price) return null;
      return ((betAmt * price) - betAmt).toFixed(2);
    }
    return null;
  })();

  const fmtAmerican = (n) => (typeof n === 'number' ? (n >= 0 ? `+${n}` : `${n}`) : n);
  const marketLabel = (key) =>
    key === 'h2h' || key === 'h2h_3way' ? 'ML' : key === 'spreads' ? 'Spread' : 'Total';

  const acceptNewOdds = (idx) => {
    const updated = slip.map((item, i) =>
      i === idx ? { ...item, price: enriched[idx].new_price, american: enriched[idx].new_american } : item
    );
    onUpdateSlip(updated);
  };

  const handlePlace = async () => {
    if (placing || betAmt <= 0 || slip.length === 0 || user?.is_read_only) return;
    setPlacing(true);
    setError(null);

    try {
      if (isParlay) {
        const legs = enriched.map((item) => ({
          event_id: item.game.odds_api_id || item.game.id.replace('oa_', ''),
          sport_key: item.game.sport_key,
          market_type: item.market.key,
          selection: item.outcome.name,
          point: item.outcome.point,
          odds_decimal: item.stale ? (item.new_price ?? item.price) : item.price,
          odds_american: item.stale ? (item.new_american ?? item.american) : item.american,
          home_team: item.game.home_team,
          away_team: item.game.away_team,
        }));
        await api.placeLiveParlay({ legs, amount: betAmt, is_free_play: useFreePlay });
        showToast?.(`${slip.length}-leg live parlay placed${useFreePlay ? ' (Free Play)' : ''}!`);
      } else {
        // Place each leg as a separate single bet
        for (const item of enriched) {
          await api.placeLiveBet({
            event_id: item.game.odds_api_id || item.game.id.replace('oa_', ''),
            sport_key: item.game.sport_key,
            market_type: item.market.key,
            selection: item.outcome.name,
            point: item.outcome.point,
            odds_decimal: item.stale ? (item.new_price ?? item.price) : item.price,
            odds_american: item.stale ? (item.new_american ?? item.american) : item.american,
            amount: betAmt,
            is_free_play: useFreePlay,
            home_team: item.game.home_team,
            away_team: item.game.away_team,
          });
        }
        showToast?.(`${slip.length === 1 ? 'Live bet' : `${slip.length} live bets`} placed${useFreePlay ? ' (Free Play)' : ''}!`);
      }

      setAmount('');
      setUseFreePlay(false);
      setIsParlayMode(false);
      onClear();
      if (onBetPlaced) onBetPlaced();
    } catch (err) {
      if (err?.response?.status === 409) {
        setError('Odds changed on one or more selections — review and try again.');
      } else {
        setError(err?.response?.data?.error || err.message || 'Failed to place bet');
      }
    } finally {
      setPlacing(false);
    }
  };

  const minBet = isParlay ? (user?.parlay_min_bet || user?.min_bet || 0.01) : (user?.live_min_bet || user?.min_bet || 0.01);
  const maxBet = isParlay ? (user?.parlay_max_bet || user?.max_bet || 0) : (user?.live_max_bet || user?.max_bet || 0);

  return (
    <div
      className="rounded-t-2xl md:rounded-lg shadow-2xl flex flex-col"
      style={{
        background: 'rgba(20,5,25,0.97)',
        border: '1px solid rgba(239,68,68,0.5)',
        maxHeight: '80vh',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(191,149,63,0.2)', background: 'rgba(0,0,0,0.3)' }}
      >
        <h3 className="text-lg font-bold" style={{ color: '#ffde82' }}>
          Live Bet Slip {slip.length > 0 && `(${slip.length})`}
        </h3>
        <button onClick={onClose} className="p-1" style={{ color: 'rgba(255,222,130,0.6)' }}>
          <X className="w-5 h-5" />
        </button>
      </div>

      {slip.length === 0 ? (
        <p className="text-center py-8 text-sm flex-1 flex items-center justify-center" style={{ color: 'rgba(255,222,130,0.5)' }}>
          Click odds to add live bets
        </p>
      ) : (
        <>
          {/* Scrollable selections list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ minHeight: 0 }}>
            {enriched.map((item, idx) => (
              <div
                key={idx}
                className="rounded-lg p-2"
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  border: item.stale ? '1px solid #f97316' : '1px solid rgba(191,149,63,0.2)',
                }}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] truncate" style={{ color: 'rgba(255,222,130,0.5)' }}>
                      🔴 {item.game.away_team} @ {item.game.home_team}
                    </div>
                    <div className="font-semibold text-xs truncate" style={{ color: '#ffde82' }}>
                      {item.outcome.name} ({marketLabel(item.market.key)})
                    </div>
                    <div className="text-xs font-medium" style={{ color: item.stale ? '#f97316' : '#BF953F' }}>
                      {fmtAmerican(item.stale ? (item.new_american ?? item.american) : item.american)}
                      {item.stale && (
                        <span className="ml-1 text-[10px]" style={{ color: '#f97316' }}>
                          <AlertTriangle size={10} className="inline mr-0.5" />updated
                        </span>
                      )}
                    </div>
                    {item.stale && (
                      <button
                        onClick={() => acceptNewOdds(idx)}
                        className="mt-1 text-[10px] px-2 py-0.5 rounded"
                        style={{ background: 'rgba(249,115,22,0.2)', border: '1px solid #f97316', color: '#f97316' }}
                      >
                        Accept new odds
                      </button>
                    )}
                  </div>
                  <button onClick={() => onRemove(idx)} className="ml-2 p-1 text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            className="p-3 flex-shrink-0 space-y-2"
            style={{ borderTop: '1px solid rgba(191,149,63,0.2)', background: 'rgba(0,0,0,0.2)' }}
          >
            {/* Parlay checkbox */}
            {slip.length >= 2 && (
              <div
                className="p-2 rounded-lg"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isParlayMode}
                    onChange={(e) => setIsParlayMode(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-xs font-medium" style={{ color: '#fca5a5' }}>
                    {slip.length}-Leg Parlay
                  </span>
                  {isParlayMode && (
                    <span className="text-xs ml-auto font-bold" style={{ color: '#4ade80' }}>
                      {parlayAmerican}
                    </span>
                  )}
                </label>
              </div>
            )}

            {/* Amount + To Win */}
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`${minBet} – ${maxBet || '∞'} ¢¢`}
                  min={minBet}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                  style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(191,149,63,0.3)', color: '#ffde82' }}
                />
              </div>
              {toWin && (
                <div className="text-right">
                  <div className="text-[10px]" style={{ color: 'rgba(255,222,130,0.5)' }}>To Win</div>
                  <div className="text-sm font-bold" style={{ color: '#4ade80' }}>{toWin}¢¢</div>
                </div>
              )}
            </div>

            {/* Free play checkbox */}
            {(freePlayBalance || 0) > 0 && (
              <div
                className="flex items-center justify-between p-2 rounded"
                style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}
              >
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useFreePlay}
                    onChange={(e) => setUseFreePlay(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-xs font-medium" style={{ color: '#d8b4fe' }}>Free Play</span>
                </label>
                <span className="text-xs font-bold" style={{ color: '#c084fc' }}>
                  {(freePlayBalance || 0).toFixed(2)}¢¢
                </span>
              </div>
            )}

            {hasStale && (
              <p className="text-xs text-orange-400 flex items-center gap-1">
                <AlertTriangle size={12} /> Some odds have changed — accept new odds above
              </p>
            )}
            {error && <p className="text-xs text-red-400">{error}</p>}

            <div className="flex gap-2">
              <button
                onClick={onClear}
                className="px-4 py-2 rounded-lg text-sm font-medium transition"
                style={{ background: 'rgba(255,255,255,0.1)', color: '#ffde82', border: '1px solid rgba(191,149,63,0.3)' }}
              >
                Clear
              </button>
              <button
                onClick={handlePlace}
                disabled={placing || betAmt <= 0 || hasStale || user?.is_read_only}
                className={`flex-1 py-2 rounded-lg font-bold transition text-sm ${
                  useFreePlay
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-gradient-to-r from-red-600 to-red-700 text-white'
                }`}
                style={(placing || betAmt <= 0 || hasStale || user?.is_read_only) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {placing ? 'Placing…' : `Place ${isParlay ? 'Parlay' : 'Bet'}${useFreePlay ? ' (FP)' : ''}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default LiveBetSlip;
